import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, Tag, Space, message, DatePicker, Switch, Descriptions, Row, Col, Popconfirm, Image } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, InfoCircleOutlined, DollarOutlined, PictureOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { adminOfferService, adminRoomService } from '../../services/adminService';
import api from '../../services/api';
import { MediaUploadField } from './MediaUploadField';
import { hasRole } from '../../utils/permissions';

const { RangePicker } = DatePicker;

// Backend offer routes use requireRole('admin', 'manager').
const canManageOffers = (): boolean => hasRole('admin', 'manager');

const OffersManagement: React.FC = () => {
  const canManage = canManageOffers();
  const [loading, setLoading] = useState(false);
  const [offers, setOffers] = useState<any[]>([]);
  const [roomTypes, setRoomTypes] = useState<any[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingOffer, setEditingOffer] = useState<any>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<any>(null);
  const [form] = Form.useForm();

  const selectedRoomTypeId = Form.useWatch('room_type_id', form);
  const selectedRoomType = roomTypes.find(rt => rt.id === selectedRoomTypeId);
  const showAcStatus = selectedRoomType?.ac_status_preference === 'both';

  const handleViewOffer = (record: any) => {
    setSelectedOffer(record);
    setDetailsVisible(true);
  };

  const fetchOffers = async () => {
    setLoading(true);
    try {
      const res: any = await api.get('/offers');
      if (res.success) {
        setOffers(res.data);
      }
    } catch (error) {
      message.error('Failed to fetch offers');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomTypes = async () => {
    try {
      const res: any = await adminRoomService.getAllRoomTypes();
      if (res.success) {
        setRoomTypes(res.data);
      }
    } catch (error) {
      console.error('Error fetching room types:', error);
    }
  };

  useEffect(() => {
    fetchOffers();
    fetchRoomTypes();
  }, []);

  const handleAdd = () => {
    setEditingOffer(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: any) => {
    setEditingOffer(record);
    form.setFieldsValue({
      ...record,
      range: [dayjs(record.start_date), dayjs(record.end_date)],
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const res: any = await api.delete(`/offers/${id}`);
      if (res.success) {
        message.success('Offer deleted');
        fetchOffers();
      }
    } catch (error) {
      message.error('Failed to delete offer');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        start_date: values.range[0].format('YYYY-MM-DD'),
        end_date: values.range[1].format('YYYY-MM-DD'),
      };
      delete payload.range;

      const res: any = editingOffer 
        ? await api.put(`/offers/${editingOffer.id}`, payload)
        : await api.post('/offers', payload);

      if (res.success) {
        message.success(`Offer ${editingOffer ? 'updated' : 'created'}`);
        setModalVisible(false);
        fetchOffers();
      }
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleOfferImageUpload = async (file: File) => {
    try {
      setLoading(true);
      const response = await adminOfferService.uploadOfferImage(file);
      const imageUrl = String((response.data as { image_url?: string })?.image_url || '');

      if (!imageUrl) {
        message.error('Upload succeeded but no image URL was returned');
        return;
      }

      form.setFieldValue('image_url', imageUrl);
      message.success('Offer image uploaded successfully');
    } catch (error) {
      console.error('Error uploading offer image:', error);
      message.error('Failed to upload offer image');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: any) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 600 }}>{text}</span>
          <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
            {record.room_type_name}
            {record.ac_status && record.ac_status !== 'any' ? ` (${record.ac_status === 'ac' ? 'AC Only' : 'Non-AC Only'})` : ''}
          </span>
        </Space>
      )
    },
    {
      title: 'Discount',
      key: 'discount',
      render: (_: any, record: any) => (
        record.discount_percentage 
          ? <Tag color="green">{record.discount_percentage}% OFF</Tag>
          : <Tag color="blue">LKR {record.discount_amount} OFF</Tag>
      )
    },
    {
      title: 'Validity',
      key: 'validity',
      render: (_: any, record: any) => (
        <span style={{ fontSize: '13px' }}>
          {dayjs(record.start_date).format('MMM DD')} - {dayjs(record.end_date).format('MMM DD, YYYY')}
        </span>
      )
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active: boolean) => (
        <Tag color={active ? 'success' : 'default'}>{active ? 'Active' : 'Inactive'}</Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Button 
          icon={<EyeOutlined />} 
          type="text" 
          onClick={(e) => { 
            e.stopPropagation(); 
            handleViewOffer(record); 
          }} 
        />
      )
    }
  ];

  return (
    <div className="offers-management">
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>Offers & Promotions</div>
          <div style={{ fontSize: 13, color: '#8c8c8c' }}>
            Create public discounts or promo-code-only promotions.
          </div>
        </div>
        {canManage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Offer
          </Button>
        )}
      </div>

      <Table 
        dataSource={offers} 
        columns={columns} 
        rowKey="id" 
        loading={loading}
        onRow={(record) => ({
          onClick: () => handleViewOffer(record),
          style: { cursor: 'pointer' }
        })}
        locale={{
          emptyText: canManage ? (
            <div style={{ padding: '24px 0', textAlign: 'center' }}>
              <p style={{ marginBottom: 12 }}>No offers created yet.</p>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                Add Offer
              </Button>
            </div>
          ) : (
            'No offers available'
          ),
        }}
        scroll={{ x: 'max-content' }}
      />

      <Modal
        title={editingOffer ? 'Edit Offer' : 'Add New Offer'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={760}
        footer={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Button onClick={() => form.resetFields()}>Reset</Button>
            <Space>
              <Button onClick={() => setModalVisible(false)}>Cancel</Button>
              <Button 
                type="primary" 
                loading={loading} 
                onClick={handleModalOk}
                style={{ backgroundColor: '#B79A68', borderColor: '#B79A68' }}
              >
                {editingOffer ? 'Save Changes' : 'Create Offer'}
              </Button>
            </Space>
          </div>
        }
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <div className="grid-two">
            <Form.Item name="title" label="Offer Title" rules={[{ required: true }]} style={{ gridColumn: '1 / -1' }}>
              <Input placeholder="e.g. Summer Special 20% Off" />
            </Form.Item>

            <Form.Item name="room_type_id" label="Applicable Room Type" rules={[{ required: true }]}>
              <Select placeholder="Select Room Type">
                {roomTypes.map(rt => (
                  <Select.Option key={rt.id} value={rt.id}>{rt.name}</Select.Option>
                ))}
              </Select>
            </Form.Item>

            {showAcStatus && (
              <Form.Item name="ac_status" label="AC Preference" initialValue="any" rules={[{ required: true }]}>
                <Select>
                  <Select.Option value="any">Any (Both AC & Non-AC)</Select.Option>
                  <Select.Option value="ac">AC Only</Select.Option>
                  <Select.Option value="non_ac">Non-AC Only</Select.Option>
                </Select>
              </Form.Item>
            )}

            <Form.Item name="range" label="Validity Period" rules={[{ required: true }]}>
              <RangePicker style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item name="discount_percentage" label="Discount %">
              <InputNumber min={0} max={100} style={{ width: '100%' }} />
            </Form.Item>
            
            <Form.Item name="discount_amount" label="Fixed Discount (LKR)">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="promo_code"
              label="Promo Code"
              extra="Leave blank for public offer"
            >
              <Input placeholder="e.g. SUMMER20" />
            </Form.Item>

            <Form.Item name="is_active" label="Status" valuePropName="checked" initialValue={true}>
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>

            <Form.Item name="description" label="Description" style={{ gridColumn: '1 / -1' }}>
              <Input.TextArea rows={3} placeholder="Describe the offer details..." />
            </Form.Item>

            <div style={{ gridColumn: '1 / -1' }}>
              <MediaUploadField
                form={form}
                fieldName="image_url"
                label="Offer Image"
                onUpload={handleOfferImageUpload}
                disabled={!canManage}
                hint="Upload an appealing image for the offer"
              />
            </div>
          </div>
        </Form>
      </Modal>

      {/* Offer Details Modal */}
      {selectedOffer && (
        <Modal
          title={`${selectedOffer.title} Details`}
          open={detailsVisible}
          onCancel={() => setDetailsVisible(false)}
          footer={
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: 16 }}>
              <Button onClick={() => setDetailsVisible(false)}>Close</Button>
              {canManage && (
                <>
                  <Popconfirm
                    title="Delete this offer?"
                    description="This action cannot be undone."
                    onConfirm={() => {
                      setDetailsVisible(false);
                      handleDelete(selectedOffer.id);
                    }}
                    okText="Delete"
                    cancelText="Cancel"
                  >
                    <Button danger icon={<DeleteOutlined />}>
                      Delete
                    </Button>
                  </Popconfirm>
                  <Button
                    type="primary"
                    style={{ backgroundColor: '#B79A68', borderColor: '#B79A68' }}
                    icon={<EditOutlined />}
                    onClick={() => {
                      setDetailsVisible(false);
                      handleEdit(selectedOffer);
                    }}
                  >
                    Edit
                  </Button>
                </>
              )}
            </div>
          }
          width={760}
          styles={{ body: { overflowX: 'hidden', padding: '16px' } }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', color: '#000' }}>
            <Row gutter={16}>
              <Col span={12}>
                <h3 style={{ fontSize: 16, color: '#B79A68', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <InfoCircleOutlined /> Offer Information
                </h3>
                <div>
                  <Descriptions column={1}>
                    <Descriptions.Item label="Title"><strong>{selectedOffer.title}</strong></Descriptions.Item>
                    <Descriptions.Item label="Room Type">
                      {selectedOffer.room_type_name}
                      {selectedOffer.ac_status && selectedOffer.ac_status !== 'any' ? ` (${selectedOffer.ac_status === 'ac' ? 'AC Only' : 'Non-AC Only'})` : ''}
                    </Descriptions.Item>
                    <Descriptions.Item label="Description">{selectedOffer.description || 'N/A'}</Descriptions.Item>
                    <Descriptions.Item label="Status">
                      <Tag color={selectedOffer.is_active ? 'success' : 'default'}>
                        {selectedOffer.is_active ? 'Active' : 'Inactive'}
                      </Tag>
                    </Descriptions.Item>
                  </Descriptions>
                </div>
              </Col>
              <Col span={12}>
                <h3 style={{ fontSize: 16, color: '#B79A68', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <DollarOutlined /> Pricing & Validity
                </h3>
                <div>
                  <Descriptions column={1}>
                    <Descriptions.Item label="Discount">
                      {selectedOffer.discount_percentage 
                        ? `${selectedOffer.discount_percentage}% OFF` 
                        : `LKR ${selectedOffer.discount_amount} OFF`}
                    </Descriptions.Item>
                    <Descriptions.Item label="Promo Code">
                      {selectedOffer.promo_code || 'N/A (Public)'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Start Date">{dayjs(selectedOffer.start_date).format('MMMM D, YYYY')}</Descriptions.Item>
                    <Descriptions.Item label="End Date">{dayjs(selectedOffer.end_date).format('MMMM D, YYYY')}</Descriptions.Item>
                  </Descriptions>
                </div>
              </Col>
            </Row>

            {selectedOffer.image_url && (
              <div>
                <h3 style={{ fontSize: 16, color: '#B79A68', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <PictureOutlined /> Offer Image
                </h3>
                <div>
                  <Image 
                    src={selectedOffer.image_url} 
                    alt="Offer Image" 
                    style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain', borderRadius: '8px' }} 
                  />
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default OffersManagement;
